import { apiPost, apiPatch } from './http'
import type {
  LoginResponse,
  MfaChallengeResponse,
  AuthTokenResponse,
  PasswordResetTokenResponse,
  MessageResponse,
} from '@/types/api'

/**
 * Typed boundary to the authentication API (sessions, second factor, password reset).
 *
 * Transport + payload shaping only — no reactive state, no token persistence
 * (that lives in `authSession.ts`) and no error-to-message mapping (that lives
 * in the calling composable/view). Mirrors the pattern of `subscription.ts`.
 */

/**
 * Outcome of `POST /sessions`. The backend answers with a session token when the
 * email second factor is off, and with a challenge (code mailed) when it is on —
 * so the caller branches on `kind` rather than on the HTTP status.
 */
export type LoginOutcome =
  | { kind: 'session'; authToken: string }
  | { kind: 'mfa_required'; mfaToken: string }

/** True when a `POST /sessions` body is the second-factor challenge, not a session. */
function isMfaChallenge(body: LoginResponse): body is MfaChallengeResponse {
  return (body as MfaChallengeResponse).mfa_required === true
}

/**
 * Step 1 of the login — check the credentials against `POST /sessions`.
 * Throws `ApiError` otherwise — the caller maps the status:
 *   401 invalid credentials · 403 account blocked · 429 rate limited.
 */
export async function login(
  email: string,
  password: string,
  signal?: AbortSignal,
): Promise<LoginOutcome> {
  const res = await apiPost<LoginResponse>('/sessions', { email, password }, { signal })
  return isMfaChallenge(res)
    ? { kind: 'mfa_required', mfaToken: res.mfa_token }
    : { kind: 'session', authToken: res.auth_token }
}

/**
 * Step 2 of the login — verify the 6-digit code mailed at step 1
 * (`POST /sessions/verify_code`). Returns the JWT auth token on success.
 * Throws `ApiError`: 401 when the code is wrong, expired (15 min), already spent
 * or out of attempts (5) — and likewise when the `mfa_token` itself expired
 * (10 min), which the backend does not distinguish; 403 when the account was
 * blocked between the two steps; 429 when rate limited.
 */
export async function verifyLoginCode(
  mfaToken: string,
  code: string,
  signal?: AbortSignal,
): Promise<string> {
  const res = await apiPost<AuthTokenResponse>(
    '/sessions/verify_code',
    { mfa_token: mfaToken, code },
    { signal },
  )
  return res.auth_token
}

/**
 * Mail a new code for a login already started (`POST /sessions/resend_code`).
 * Returns the replacement `mfa_token` — the previous one must be dropped.
 * Throws `ApiError` with status 401 when the challenge has expired: the whole
 * login has to start over.
 */
export async function resendLoginCode(mfaToken: string, signal?: AbortSignal): Promise<string> {
  const res = await apiPost<MfaChallengeResponse>(
    '/sessions/resend_code',
    { mfa_token: mfaToken },
    { signal },
  )
  return res.mfa_token
}

/**
 * Step 1 of the password reset — request a verification code by email
 * (`POST /passwords/forgot`). The backend always answers 200 to avoid account
 * enumeration, so a resolved promise does not imply the email exists.
 */
export async function forgotPassword(email: string, signal?: AbortSignal): Promise<void> {
  await apiPost<MessageResponse>('/passwords/forgot', { email }, { signal })
}

/**
 * Step 2 of the password reset — verify the 6-digit code
 * (`POST /passwords/verify_code`). Returns the password reset token on success.
 * Throws `ApiError` with status 401 when the code is wrong or expired (15 min).
 */
export async function verifyResetCode(email: string, code: string, signal?: AbortSignal): Promise<string> {
  const res = await apiPost<PasswordResetTokenResponse>('/passwords/verify_code', { email, code }, { signal })
  return res.password_reset_token
}

/**
 * Step 3 of the password reset — set the new password (`PATCH /passwords`).
 * Throws `ApiError`: 422 when the password fails validation (min 12 chars,
 * complexity, confirmation mismatch), 401 when the reset token is invalid/expired.
 */
export async function resetPassword(
  passwordResetToken: string,
  password: string,
  passwordConfirmation: string,
  signal?: AbortSignal,
): Promise<void> {
  await apiPatch<MessageResponse>(
    '/passwords',
    {
      password_reset_token: passwordResetToken,
      password,
      password_confirmation: passwordConfirmation,
    },
    { signal },
  )
}
